// E-Mail – gemeinsame Begriffe für Server, Oberfläche und Verträge (docs/anmeldung/SPEZIFIKATION.md §4, §7.1).
// Läuft unverändert in Browser und Node: keine Node- oder DOM-Typen, nur zod.
//
//   Zustände, Bezeichnungen   MAIL_STATUSES · MAIL_STATUS_LABELS · MAIL_RECIPIENT_STATUSES · MAIL_TRANSPORT_LABELS …
//   Adressen                  normalizeMailAddress · mailDomainToAscii · isReservedMailDomain · maskMailAddress
//   Vorlagen                  parseMailTemplate · checkMailTemplateText · renderMailTemplate · KERNEL_MAIL_VARIABLES
//   Formen (Zod)              zMailAddress · zMailAddressText · zMailBlock · zMailSummary · zUnsubscribe…
//
// Die Datenbank prüft dieselben Regeln noch einmal (0100_mail.sql: app_mail_address_ok, Kopf-Regeln) – was hier
// durchgeht, kann dort nicht scheitern, und umgekehrt schützt die Datenbank, wenn ein Weg diese Prüfung vergisst.
import { z } from 'zod'
import { zId } from './ids'

// ---- Zustände ------------------------------------------------------------------------------------------

/**
 * Zustand einer Nachricht im Postausgang (mail_message.status). Vorwärts nur so (Wächter in 0100):
 *   queued → sending | cancelled | held        sending → sent | retry | failed | held | queued
 *   retry → sending | cancelled | failed       held → queued | cancelled | failed
 * `sent`, `failed`, `cancelled`, `suppressed` sind Endzustände.
 */
export const MAIL_STATUSES = ['queued', 'sending', 'retry', 'held', 'sent', 'failed', 'cancelled', 'suppressed'] as const
export type MailStatus = (typeof MAIL_STATUSES)[number]

export const MAIL_STATUS_LABELS: Record<MailStatus, string> = {
  queued: 'Vorgemerkt',
  sending: 'Wird gesendet',
  retry: 'Neuer Versuch geplant',
  held: 'Gesammelt – kein Versandweg',
  sent: 'Vom Mailserver angenommen',
  failed: 'Nicht zugestellt',
  cancelled: 'Abgebrochen',
  suppressed: 'Nicht versendet (gesperrt)',
}

/** Farbton für Statusmarken der Oberfläche (Badge-Töne aus @ui) */
export const MAIL_STATUS_TONES: Record<MailStatus, 'neutral' | 'info' | 'success' | 'warning' | 'danger'> = {
  queued: 'info',
  sending: 'info',
  retry: 'warning',
  held: 'warning',
  sent: 'success',
  failed: 'danger',
  cancelled: 'neutral',
  suppressed: 'neutral',
}

export const MAIL_FINAL_STATUSES: readonly MailStatus[] = ['sent', 'failed', 'cancelled', 'suppressed']
/** Abbrechen (platform.mail.cancel, app_mail_cancel) geht nur vor dem Versand */
export const MAIL_CANCELLABLE_STATUSES: readonly MailStatus[] = ['queued', 'retry', 'held']
export const isFinalMailStatus = (status: MailStatus): boolean => MAIL_FINAL_STATUSES.includes(status)

/** Zustand je Empfänger (mail_recipient.status) */
export const MAIL_RECIPIENT_STATUSES = ['pending', 'accepted', 'rejected', 'bounced', 'suppressed'] as const
export type MailRecipientStatus = (typeof MAIL_RECIPIENT_STATUSES)[number]
export const MAIL_RECIPIENT_STATUS_LABELS: Record<MailRecipientStatus, string> = {
  pending: 'Ausstehend',
  accepted: 'Angenommen',
  rejected: 'Abgelehnt',
  bounced: 'Unzustellbar',
  suppressed: 'Gesperrt',
}

export const MAIL_RECIPIENT_KINDS = ['to', 'cc', 'bcc'] as const
export type MailRecipientKind = (typeof MAIL_RECIPIENT_KINDS)[number]
export const MAIL_RECIPIENT_KIND_LABELS: Record<MailRecipientKind, string> = { to: 'An', cc: 'Kopie', bcc: 'Blindkopie' }

/** Versandweg der Installation (MERXO_MAIL_TRANSPORT, §4.7) */
export const MAIL_TRANSPORTS = ['smtp', 'capture', 'off'] as const
export type MailTransportKind = (typeof MAIL_TRANSPORTS)[number]
export const MAIL_TRANSPORT_LABELS: Record<MailTransportKind, string> = {
  smtp: 'Mailserver (SMTP)',
  capture: 'In der Fangstelle abgelegt – nicht verschickt',
  off: 'Kein Versandweg eingerichtet',
}

/** Ergebnis eines Versuchs (mail_attempt.outcome) */
export const MAIL_ATTEMPT_OUTCOMES = ['accepted', 'temporary', 'permanent', 'config', 'expired'] as const
export type MailAttemptOutcome = (typeof MAIL_ATTEMPT_OUTCOMES)[number]
export const MAIL_ATTEMPT_OUTCOME_LABELS: Record<MailAttemptOutcome, string> = {
  accepted: 'Angenommen',
  temporary: 'Vorübergehend abgelehnt',
  permanent: 'Endgültig abgelehnt',
  config: 'Versandweg falsch eingerichtet',
  expired: 'Abgelaufen',
}

/** Kanal, über den eine Nachricht vorgemerkt wurde (mail_message.channel) */
export const MAIL_CHANNELS = ['ui', 'api', 'assistant', 'system', 'import'] as const
export type MailChannel = (typeof MAIL_CHANNELS)[number]

// ---- Absender, Antwort-An, Sperrliste -----------------------------------------------------------------------

/** Zweck eines Absenders (mail_sender.purpose, §4.4) */
export const MAIL_SENDER_PURPOSES = ['documents', 'dunning', 'purchasing', 'account', 'system'] as const
export type MailSenderPurpose = (typeof MAIL_SENDER_PURPOSES)[number]
export const MAIL_SENDER_PURPOSE_LABELS: Record<MailSenderPurpose, string> = {
  documents: 'Belege (Rechnungen, Angebote, Lieferscheine)',
  dunning: 'Mahnungen',
  purchasing: 'Bestellungen an Lieferanten',
  account: 'Einladungen',
  system: 'Systemmeldungen',
}

/** Regel für „Antwort an“ (mail_setting.reply_to_mode) */
export const MAIL_REPLY_TO_MODES = ['sender', 'actor', 'fixed'] as const
export type MailReplyToMode = (typeof MAIL_REPLY_TO_MODES)[number]
export const MAIL_REPLY_TO_MODE_LABELS: Record<MailReplyToMode, string> = {
  sender: 'An den Absender',
  actor: 'An die sendende Person',
  fixed: 'An eine feste Adresse',
}

/** Grund eines Sperrlisten-Eintrags (mail_suppression.reason) */
export const MAIL_SUPPRESSION_REASONS = ['hard_bounce', 'manual', 'unsubscribed', 'invalid'] as const
export type MailSuppressionReason = (typeof MAIL_SUPPRESSION_REASONS)[number]
export const MAIL_SUPPRESSION_REASON_LABELS: Record<MailSuppressionReason, string> = {
  hard_bounce: 'Adresse unzustellbar',
  manual: 'Von Hand gesperrt',
  unsubscribed: 'Abbestellt',
  invalid: 'Ungültige Adresse',
}

// ---- Kategorien, Vorrang, Grenzen -------------------------------------------------------------------------

/** Kategorie = Vorlage: '<modul>.<name>' bzw. für den Kern 'account.*' und 'system.*' */
export const MAIL_CATEGORY_PATTERN = /^[a-z][a-z0-9]*\.[a-z][a-z0-9_]*$/

/** Kategorien des Kerns (Kontoschreiben und Systemmeldungen, §4.2) – Module melden ihre über defineMailTemplate an */
export const KERNEL_MAIL_CATEGORIES = {
  invitation: 'account.invitation',
  passwordReset: 'account.password_reset',
  security: 'account.security',
  notification: 'system.notification',
  opsAlert: 'system.ops_alert',
} as const
export type KernelMailCategory = (typeof KERNEL_MAIL_CATEGORIES)[keyof typeof KERNEL_MAIL_CATEGORIES]
/** Präfixe, die nur der Kern vergibt */
export const KERNEL_MAIL_PREFIXES = ['account', 'system'] as const
export const isKernelMailCategory = (category: string): boolean =>
  (KERNEL_MAIL_PREFIXES as readonly string[]).includes(category.split('.')[0] ?? '')

/** Vorrang beim Zustellen (mail_message.priority, kleiner = früher) */
export const MAIL_PRIORITY = { account: 1, documents: 5, system: 7 } as const

/** Grenzen einer Nachricht (§4.1, §4.3) */
export const MAIL_LIMITS = {
  /** to + cc + bcc zusammen */
  recipients: 10,
  /** Anhänge zusammen */
  attachmentBytes: 15 * 1024 * 1024,
  /** persönliche Nachricht der sendenden Person */
  message: 1000,
  /** Betreff nach dem Einsetzen der Platzhalter */
  subject: 200,
  /** Vorlage: Einleitung bzw. Schluss */
  intro: 4000,
  closing: 2000,
  /** Signatur des Betriebs (mail_setting.signature) */
  signature: 1000,
  /** Länge einer Adresse (RFC 5321) */
  address: 254,
  /** Anzeigename eines Absenders */
  fromName: 120,
} as const

/** Rückgängig-Fenster für Belegmails in Sekunden (mail_setting.undo_seconds, §4.6) */
export const MAIL_UNDO_SECONDS = { default: 20, min: 0, max: 120 } as const

/** Wiederholung nach Versuch n (Minuten); Endzustand failed nach MAIL_RETRY.maxAttempts bzw. maxAgeHours */
export const MAIL_RETRY = { minutes: [1, 5, 15, 30, 60, 120, 240, 480], maxAttempts: 10, maxAgeHours: 24 } as const

/** Minuten bis zum nächsten Versuch nach `attempts` Versuchen (1-basiert); null = kein weiterer Versuch */
export function mailRetryDelayMinutes(attempts: number): number | null {
  if (!Number.isInteger(attempts) || attempts < 1 || attempts >= MAIL_RETRY.maxAttempts) return null
  const schedule = MAIL_RETRY.minutes
  return schedule[Math.min(attempts, schedule.length) - 1] ?? null
}

/** Belegdatei-Art einer versandten E-Mail (document_file.kind, §4.9) */
export const MAIL_DOCUMENT_FILE_KIND = 'email'

// ---- Adressen ---------------------------------------------------------------------------------------------

export type MailAddressCheck = { ok: true; address: string } | { ok: false; problem: string }

export const MAIL_ADDRESS_TEXTS = {
  empty: 'Bitte geben Sie eine E-Mail-Adresse an.',
  invalid: 'Bitte geben Sie eine gültige E-Mail-Adresse an (z. B. name@firma.de).',
  lineBreak: 'Die E-Mail-Adresse darf keine Zeilenumbrüche enthalten.',
  tooLong: 'Die E-Mail-Adresse ist zu lang (höchstens 254 Zeichen).',
  localPart: 'Vor dem @ sind nur Buchstaben ohne Umlaute, Ziffern und die Zeichen . _ - + erlaubt.',
} as const

// Zeichen vor dem @ (RFC 5322 „atext“, ohne Anführungszeichen-Formen) – nur ASCII, kein SMTPUTF8 (§4.5)
const LOCAL_PART = /^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*$/
const DOMAIN_LABEL = /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)$/
const TOP_LABEL = /^(?:[a-z]{2,63}|xn--[a-z0-9-]{1,59})$/
const ASCII = /^[\x20-\x7e]*$/

/**
 * Prüft und vereinheitlicht eine Adresse: klein geschrieben, Domäne in ASCII (Punycode nach RFC 3492), höchstens
 * 254 Zeichen, keine Zeilenumbrüche, Teil vor dem @ nur ASCII. Gleiche Regel wie app_mail_address_ok() in 0100.
 */
export function normalizeMailAddress(raw: string): MailAddressCheck {
  if (typeof raw !== 'string') return { ok: false, problem: MAIL_ADDRESS_TEXTS.invalid }
  if (/[\r\n\0]/.test(raw)) return { ok: false, problem: MAIL_ADDRESS_TEXTS.lineBreak }
  const trimmed = raw.trim()
  if (trimmed === '') return { ok: false, problem: MAIL_ADDRESS_TEXTS.empty }
  if (/\s/.test(trimmed)) return { ok: false, problem: MAIL_ADDRESS_TEXTS.invalid }
  const at = trimmed.lastIndexOf('@')
  if (at <= 0 || at === trimmed.length - 1) return { ok: false, problem: MAIL_ADDRESS_TEXTS.invalid }
  const local = trimmed.slice(0, at).toLowerCase()
  if (!ASCII.test(local)) return { ok: false, problem: MAIL_ADDRESS_TEXTS.localPart }
  if (local.length > 64 || !LOCAL_PART.test(local)) return { ok: false, problem: MAIL_ADDRESS_TEXTS.invalid }
  const domain = mailDomainToAscii(trimmed.slice(at + 1))
  if (domain === null) return { ok: false, problem: MAIL_ADDRESS_TEXTS.invalid }
  const address = `${local}@${domain}`
  if (address.length > MAIL_LIMITS.address) return { ok: false, problem: MAIL_ADDRESS_TEXTS.tooLong }
  return { ok: true, address }
}

/** Domäne in ASCII (IDNA, Punycode) und klein – null, wenn sie keine gültige Domäne mit Punkt ist. */
export function mailDomainToAscii(raw: string): string | null {
  const domain = raw.normalize('NFC').toLowerCase().replace(/[。．｡]/g, '.')
  if (domain === '' || domain.length > 253 || domain.endsWith('.')) return null
  const labels = domain.split('.')
  if (labels.length < 2) return null
  const ascii: string[] = []
  for (const label of labels) {
    const encoded = ASCII.test(label) ? label : `xn--${punycodeEncode(label)}`
    if (encoded.length > 63 || !DOMAIN_LABEL.test(encoded)) return null
    ascii.push(encoded)
  }
  if (!TOP_LABEL.test(ascii[ascii.length - 1] as string)) return null
  const result = ascii.join('.')
  return result.length > 253 ? null : result
}

/** Domäne einer (geprüften) Adresse */
export const mailDomainOf = (address: string): string => address.slice(address.lastIndexOf('@') + 1).toLowerCase()

/** Reservierte Domänen (RFC 2606, 6761): an sie geht nie eine Nachricht – sie werden `suppressed` (§4.1 Regel 4). */
const RESERVED_TLDS = ['example', 'test', 'invalid', 'localhost']
const RESERVED_DOMAINS = ['example.com', 'example.net', 'example.org']
export function isReservedMailDomain(addressOrDomain: string): boolean {
  const domain = mailDomainOf(addressOrDomain)
  const top = domain.slice(domain.lastIndexOf('.') + 1)
  return RESERVED_TLDS.includes(top) || RESERVED_DOMAINS.some((reserved) => domain === reserved || domain.endsWith(`.${reserved}`))
}

/** Für Hinweise ohne Preisgabe: 'lea.huber@altinger.example' → 'l…r@altinger.example' (§6.3) */
export function maskMailAddress(address: string): string {
  const at = address.lastIndexOf('@')
  if (at <= 0) return '…'
  const local = address.slice(0, at)
  const masked = local.length <= 2 ? `${local[0]}…` : `${local[0]}…${local[local.length - 1]}`
  return `${masked}${address.slice(at)}`
}

/** Anzeigename für Kopfzeilen: ohne Steuerzeichen, Leerraum zusammengezogen, höchstens 120 Zeichen. */
export function cleanMailDisplayName(name: string | null | undefined): string | null {
  if (name === null || name === undefined) return null
  const cleaned = name.replace(/[\u0000-\u001f\u007f]+/g, ' ').replace(/\s+/g, ' ').trim()
  return cleaned === '' ? null : cleaned.slice(0, MAIL_LIMITS.fromName)
}

// ---- Punycode (RFC 3492) – Kodieren genügt: Merxo liest keine fremden IDN-Adressen zurück --------------------

const PUNY = { base: 36, tMin: 1, tMax: 26, skew: 38, damp: 700, initialBias: 72, initialN: 128 } as const

function punyAdapt(delta: number, points: number, first: boolean): number {
  let d = first ? Math.floor(delta / PUNY.damp) : delta >> 1
  d += Math.floor(d / points)
  let k = 0
  while (d > ((PUNY.base - PUNY.tMin) * PUNY.tMax) >> 1) {
    d = Math.floor(d / (PUNY.base - PUNY.tMin))
    k += PUNY.base
  }
  return k + Math.floor(((PUNY.base - PUNY.tMin + 1) * d) / (d + PUNY.skew))
}

const punyDigit = (d: number): string => String.fromCharCode(d < 26 ? 97 + d : 22 + d)

/** Ein Label ohne „xn--“: 'münchen' → 'mnchen-3ya' */
export function punycodeEncode(label: string): string {
  const points = Array.from(label, (ch) => ch.codePointAt(0) as number)
  let n: number = PUNY.initialN
  let delta = 0
  let bias: number = PUNY.initialBias
  let output = points
    .filter((cp) => cp < 0x80)
    .map((cp) => String.fromCharCode(cp))
    .join('')
  const basic = output.length
  let handled = basic
  if (basic > 0) output += '-'
  while (handled < points.length) {
    let next = Number.MAX_SAFE_INTEGER
    for (const cp of points) if (cp >= n && cp < next) next = cp
    delta += (next - n) * (handled + 1)
    n = next
    for (const cp of points) {
      if (cp < n) delta++
      if (cp === n) {
        let q = delta
        for (let k = PUNY.base; ; k += PUNY.base) {
          const t = k <= bias ? PUNY.tMin : k >= bias + PUNY.tMax ? PUNY.tMax : k - bias
          if (q < t) break
          output += punyDigit(t + ((q - t) % (PUNY.base - t)))
          q = Math.floor((q - t) / (PUNY.base - t))
        }
        output += punyDigit(q)
        bias = punyAdapt(delta, handled + 1, handled === basic)
        delta = 0
        handled++
      }
    }
    delta++
    n++
  }
  return output
}

// ---- Vorlagen: Platzhalter {{name}} und Abschnitte {{#name}}…{{/name}} – sonst nichts (§4.3) ---------------------

/** Platzhalter, die der Kern in jeder Nachricht setzt (§4.3) */
export const KERNEL_MAIL_VARIABLES: Readonly<Record<string, string>> = {
  'betrieb.name': 'Name des Betriebs',
  'betrieb.telefon': 'Telefon des Betriebs',
  'betrieb.email': 'E-Mail-Adresse des Betriebs',
  'standort.name': 'Standort',
  'person.name': 'Name der sendenden Person',
  heute: 'Heutiges Datum',
  produkt: 'Name des Produkts',
}

export const MAIL_VARIABLE_PATTERN = /^[a-z][a-z0-9_]*(?:\.[a-z][a-z0-9_]*)*$/

export type MailTemplateNode =
  | { kind: 'text'; text: string }
  | { kind: 'var'; name: string }
  | { kind: 'section'; name: string; children: MailTemplateNode[] }

export type MailTemplateParse = { ok: true; nodes: MailTemplateNode[] } | { ok: false; problem: string }

/** Zerlegt einen Vorlagentext. Fehler sind fertige Sätze für den Vorlagen-Editor (Feldfehler). */
export function parseMailTemplate(text: string): MailTemplateParse {
  // {{{…}}} hieße in anderen Vorlagensprachen „rohes HTML“ – das gibt es hier nicht, also auch keine Verwechslung
  if (text.includes('{{{') || text.includes('}}}')) return { ok: false, problem: 'Dreifache geschweifte Klammern gibt es nicht – Werte werden immer als Text eingesetzt.' }
  const root: MailTemplateNode[] = []
  const stack: { name: string; children: MailTemplateNode[] }[] = []
  const target = (): MailTemplateNode[] => stack[stack.length - 1]?.children ?? root
  const tag = /\{\{\s*([#/]?)\s*([^{}]*?)\s*\}\}/g
  let last = 0
  for (const match of text.matchAll(tag)) {
    const before = text.slice(last, match.index)
    if (before.includes('{{') || before.includes('}}')) return { ok: false, problem: 'Ein Platzhalter ist nicht richtig geschlossen – bitte prüfen Sie die geschweiften Klammern.' }
    if (before) target().push({ kind: 'text', text: before })
    last = match.index + match[0].length
    const marker = match[1] as string
    const name = match[2] as string
    if (!MAIL_VARIABLE_PATTERN.test(name)) return { ok: false, problem: `„${match[0]}“ ist kein gültiger Platzhalter.` }
    if (marker === '#') {
      const section = { kind: 'section' as const, name, children: [] as MailTemplateNode[] }
      target().push(section)
      stack.push(section)
    } else if (marker === '/') {
      const open = stack.pop()
      if (!open || open.name !== name) return { ok: false, problem: `Der Abschnitt {{/${name}}} schließt keinen offenen Abschnitt.` }
    } else target().push({ kind: 'var', name })
  }
  const rest = text.slice(last)
  if (rest.includes('{{') || rest.includes('}}')) return { ok: false, problem: 'Ein Platzhalter ist nicht richtig geschlossen – bitte prüfen Sie die geschweiften Klammern.' }
  if (rest) target().push({ kind: 'text', text: rest })
  const open = stack.pop()
  if (open) return { ok: false, problem: `Der Abschnitt {{#${open.name}}} wird nicht mit {{/${open.name}}} geschlossen.` }
  return { ok: true, nodes: root }
}

/** Alle Namen (Platzhalter und Abschnitte) eines zerlegten Textes */
export function mailTemplateNames(nodes: readonly MailTemplateNode[]): string[] {
  const names = new Set<string>()
  const walk = (list: readonly MailTemplateNode[]): void => {
    for (const node of list) {
      if (node.kind === 'var') names.add(node.name)
      else if (node.kind === 'section') {
        names.add(node.name)
        walk(node.children)
      }
    }
  }
  walk(nodes)
  return [...names]
}

/**
 * Prüft einen Text des Vorlagen-Editors: Länge, Klammern, nur bekannte Platzhalter (die der Vorlage und
 * KERNEL_MAIL_VARIABLES). Ergebnis: null = in Ordnung, sonst ein Satz für das Feld.
 */
export function checkMailTemplateText(text: string, options: { variables: readonly string[]; max: number; singleLine?: boolean }): string | null {
  if (text.length > options.max) return `Der Text ist zu lang (höchstens ${options.max.toLocaleString('de-DE')} Zeichen).`
  if (options.singleLine && /[\r\n]/.test(text)) return 'Der Betreff steht in einer Zeile.'
  const parsed = parseMailTemplate(text)
  if (!parsed.ok) return parsed.problem
  const known = new Set([...options.variables, ...Object.keys(KERNEL_MAIL_VARIABLES)])
  const unknown = mailTemplateNames(parsed.nodes).filter((name) => !known.has(name))
  if (unknown.length > 0) return `Den Platzhalter {{${unknown[0]}}} gibt es in dieser Vorlage nicht.`
  return null
}

/**
 * Setzt Werte ein. `escape` maskiert jeden Wert (HTML-Fassung); die Textfassung lässt ihn wörtlich. Ein Abschnitt
 * erscheint nur, wenn sein Wert nicht leer ist (null, undefined, '' und nur Leerraum gelten als leer).
 */
export function renderMailTemplate(
  nodes: readonly MailTemplateNode[],
  values: Readonly<Record<string, string | null | undefined>>,
  escape: (value: string) => string = (value) => value,
): string {
  let out = ''
  for (const node of nodes) {
    if (node.kind === 'text') out += node.text
    else if (node.kind === 'var') out += escape(values[node.name] ?? '')
    else if ((values[node.name] ?? '').trim() !== '') out += renderMailTemplate(node.children, values, escape)
  }
  return out
}

// ---- Formen für Verträge und HTTP (Zod) ----------------------------------------------------------------------

/** Adresse als Text – Ausgabe ist die vereinheitlichte Form (klein, Domäne in ASCII) */
export const zMailAddressText = z.string().transform((value, ctx) => {
  const checked = normalizeMailAddress(value)
  if (!checked.ok) {
    ctx.addIssue({ code: 'custom', message: checked.problem })
    return z.NEVER
  }
  return checked.address
})

/** Empfänger bzw. Absender: Adresse und Anzeigename */
export const zMailAddress = z.strictObject({
  address: zMailAddressText,
  name: z
    .string()
    .max(MAIL_LIMITS.fromName, { error: `Der Name ist zu lang (höchstens ${MAIL_LIMITS.fromName} Zeichen).` })
    .refine((value) => !/[\r\n\0]/.test(value), { error: 'Der Name darf keine Zeilenumbrüche enthalten.' })
    .nullish(),
})
export type MailAddress = { address: string; name?: string | null }

/** Persönliche Zeile der sendenden Person (Klartext) */
export const zMailMessageText = z
  .string()
  .trim()
  .max(MAIL_LIMITS.message, { error: `Die Nachricht ist zu lang (höchstens ${MAIL_LIMITS.message} Zeichen).` })

const zAlign = z.enum(['left', 'right'])
/** Pfad der Oberfläche für Schaltflächen (§4.1): beginnt mit /, ohne Schema, Host, Fragment */
export const zMailPath = z.string().regex(/^\/(?!\/)[A-Za-z0-9\-._~!$&'()*+,;=:@/?%]*$/, { error: 'Nur Pfade der Oberfläche (beginnend mit /) sind erlaubt.' })

/** Inhaltsblöcke nach der Einleitung – nur Struktur, HTML und Text baut der Kern (§4.1) */
export const zMailBlock = z.discriminatedUnion('kind', [
  z.strictObject({ kind: z.literal('paragraph'), text: z.string() }),
  z.strictObject({ kind: z.literal('facts'), rows: z.array(z.strictObject({ label: z.string(), value: z.string() })) }),
  z.strictObject({
    kind: z.literal('table'),
    columns: z.array(z.strictObject({ key: z.string(), label: z.string(), align: zAlign.optional() })).min(1),
    rows: z.array(z.record(z.string(), z.string())),
    footer: z.array(z.record(z.string(), z.string())).optional(),
  }),
  z.strictObject({
    kind: z.literal('totals'),
    rows: z.array(z.strictObject({ label: z.string(), value: z.string(), strong: z.boolean().optional() })),
  }),
  z.strictObject({ kind: z.literal('note'), text: z.string(), tone: z.enum(['info', 'warning']).optional() }),
  z.strictObject({ kind: z.literal('button'), label: z.string().min(1).max(80), path: zMailPath }),
  // Pfad + '#token=<Geheimnis>' – das Geheimnis setzt erst der Zusteller ein (nie in mail_message.content)
  z.strictObject({ kind: z.literal('secretLink'), label: z.string().min(1).max(80), path: zMailPath }),
])
export type MailBlock = z.infer<typeof zMailBlock>

/** Versandstand am Datensatz bzw. Zeile des Postausgangs (platform.mail.forEntity, platform.mail.list) */
export const zMailSummary = z.object({
  mailId: zId,
  category: z.string(),
  /** Bezeichnung der Vorlage („Rechnung“) */
  categoryTitle: z.string(),
  status: z.enum(MAIL_STATUSES),
  transport: z.enum(MAIL_TRANSPORTS).nullable(),
  subject: z.string(),
  recipients: z.array(
    z.object({
      address: z.string(),
      name: z.string().nullable(),
      kind: z.enum(MAIL_RECIPIENT_KINDS),
      status: z.enum(MAIL_RECIPIENT_STATUSES),
      /** Antwort des Mailservers, gekürzt (300 Zeichen) */
      response: z.string().nullable(),
    }),
  ),
  entity: z.object({ type: z.string(), id: zId, label: z.string().nullable() }).nullable(),
  documentId: zId.nullable(),
  actorUserId: zId.nullable(),
  queuedAt: z.date(),
  notBefore: z.date(),
  sentAt: z.date().nullable(),
  finalAt: z.date().nullable(),
  /** deutscher Satz bei failed/held/suppressed */
  failureReason: z.string().nullable(),
  attachmentCount: z.number().int().nonnegative(),
  resentOf: zId.nullable(),
})
export type MailSummary = z.infer<typeof zMailSummary>

// ---- Abmelden (§4.8): öffentliche Seite /mail/unsubscribe#token=… -------------------------------------------

/** Abmelde-Token: Form prüft nur die Länge und Zeichen – Inhalt und Signatur prüft der Kern (HMAC) */
export const zUnsubscribeToken = z.string().regex(/^[A-Za-z0-9._~-]{20,600}$/, { error: 'Der Abmelde-Link ist ungültig.' })
/** POST /api/public/mail/unsubscribe/info { token } – Seite nennt Kategorie und Betrieb vor dem Abbestellen */
export const zUnsubscribeInfoBody = z.strictObject({ token: zUnsubscribeToken })
export const zUnsubscribeInfoResult = z.object({
  categoryTitle: z.string(),
  tenantName: z.string(),
  /** bereits abbestellt */
  alreadyOff: z.boolean(),
})
/** POST /api/public/mail/unsubscribe { token } bzw. Formular List-Unsubscribe=One-Click */
export const zUnsubscribeBody = z.strictObject({ token: zUnsubscribeToken })
export const zUnsubscribeResult = z.object({ ok: z.literal(true) })
export type UnsubscribeInfo = z.infer<typeof zUnsubscribeInfoResult>

/** Öffentliche Routen des Mail-Kerns (unter /api), Router: server/kernel/mail/http.ts */
export const MAIL_PUBLIC_ROUTES = {
  unsubscribeInfo: '/public/mail/unsubscribe/info',
  unsubscribe: '/public/mail/unsubscribe',
} as const
/** Seiten der Oberfläche, die Links aus Mails öffnen (Fragment #token=…) */
export const MAIL_LINK_PATHS = {
  passwordReset: '/password/reset',
  invitation: '/invitation',
  unsubscribe: '/mail/unsubscribe',
  devices: '/settings/profile#geraete',
} as const
